import {
    getNumberAsCurrency,
    getNumberWithTwoDecimal,
} from '@helpers/miscelleanous';
import { isinRegexPattern } from '@helpers/regex-patterns';
import routes from '@helpers/routes';
import { isValidResponseEntity } from '@helpers/utils';
import useWindowDimensions from '@hooks/useWindowDimensions';
import {
    getBondDetailsByIsin,
    getBondSearchResults,
} from '@services/bond-details.service';
import { debounce } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

const useBondDirectorySearch = () => {
    const router = useRouter();

    const searchViewRef = useRef(null);

    const { width } = useWindowDimensions();

    const [loading, setLoading] = useState({
        search: false,
        results: false,
        searchButton: false,
    });
    const [errors, setErrors] = useState(null);
    const [searchError, setSearchError] = useState(null);

    const [searchQuery, setSearchQuery] = useState(router.query.search || '');
    const [isIsin, setIsIsin] = useState(false);

    const [showSearchModal, setShowSearchModal] = useState(false);
    const [suggestions, setSuggestions] = useState({
        search: null,
        suggestions: null,
        showSuggestions: false,
    });
    const [searchResults, setSearchResults] = useState({
        search: null,
        results: null,
        page: 1,
    });
    const [showResultsPage, setShowResultsPage] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);

    const [hasMore, setHasMore] = useState(false);

    const handlePageChange = (page) => {
        const searchQuery = router.query.search;
        setLoading((prev) => ({
            ...prev,
            results: true,
        }));

        window.scrollTo(0, 0);
        fetchSearchResults(
            {
                value: searchQuery,
                pageNumber: page,
            },
            false
        );
    };

    const fetchSuggestions = async (
        search,
        page = 1,
        showSuggestions = true
    ) => {
        setErrors(null);
        const input = search.trim();
        if (!input || input.length < 3) {
            setSuggestions({
                search: input,
                suggestions: null,
                showSuggestions: false,
            });
            return;
        }

        if (suggestions.suggestions && suggestions.search === input) {
            setSuggestions({
                search: input,
                suggestions: suggestions.suggestions,
                showSuggestions: false,
            });
            return;
        }

        if (!showSuggestions) {
            setSuggestions({
                search: input,
                suggestions: suggestions.suggestions,
                showSuggestions: false,
            });

            return;
        }

        setLoading((prev) => ({
            ...prev,
            search: true,
        }));
        try {
            const isISIN = isinRegexPattern.test(input);
            const payload = {
                isin: isISIN ? input.toUpperCase() : null,
                issuer: !isISIN ? input : null,
                isIsin: isISIN,
                page,
                limit: 12,
            };

            if (input && isISIN === false && !payload.issuer) {
                // This is to ensure that the payload is not empty
                return;
            }

            const data = await getBondDetailsByIsin(payload);

            if (isValidResponseEntity(data)) {
                const hasMoreResults = data.entity.count > 12;
                const formattedData = data.entity.rows.map(
                    ({ id, isin, issuer, opportunityId }) => ({
                        id,
                        isin,
                        issuer,
                        opportunityId,
                    })
                );

                setSuggestions({
                    search: input,
                    suggestions: formattedData,
                    showSuggestions: true,
                    error: null,
                });
                setHasMore(hasMoreResults);
            } else {
                setSuggestions({
                    search: input,
                    suggestions: null,
                    showSuggestions: false,
                    error: 'Search Failed. Please try again.',
                });
                setErrors(true);
                setHasMore(false);
            }
        } catch (error) {
            console.error('Search Failed. Please try again.:', error);
            setErrors(true);
            setSuggestions({
                search: input,
                suggestions: null,
                showSuggestions: true,
                error: 'Search Failed. Please try again.',
            });
        } finally {
            setLoading({
                search: false,
                searchButton: false,
                results: false,
            });
        }
    };

    const fetchSearchResults = async (
        searchPayload,
        isButtonSearch = false
    ) => {
        const { value, pageNumber = 1 } = searchPayload;
        const page = Number(pageNumber);
        const input = value.trim();
        setSearchError(null);
        setLoading((prev) => {
            return {
                search: false,
                results: true,
                searchButton: isButtonSearch,
            };
        });
        setSuggestions((prev) => ({
            ...prev,
            showSuggestions: false,
        }));
        setSearchResults({
            search: searchQuery,
            results: null,
            page: pageNumber,
        });
        if (!input || input.length < 3) {
            setSearchResults({
                search: input,
                results: {
                    rows: [],
                    count: 0,
                },
                showSuggestions: false,
            });
            setSearchQuery('');

            setLoading({
                search: false,
                results: false,
                searchButton: false,
            });
            return;
        }
        if (
            searchResults.search === input &&
            searchResults.results &&
            searchResults.page === page
        ) {
            setSearchQuery(input);

            setShowResultsPage(true);
            setShowSearchModal(false);
            setSearchResults({
                search: input,
                results: searchResults.results,
                page: page,
            });
            setLoading({
                search: false,
                results: false,
                searchButton: false,
            });
            return;
        }

        setShowResultsPage(true);
        setShowSearchModal(false);

        try {
            const payload = {
                issuer: input,
                page,
                limit: 12,
            };

            if (!payload.issuer) {
                return;
            }
            const data = await getBondSearchResults(payload);

            if (isValidResponseEntity(data)) {
                const formattedData = data.entity.rows.map(
                    ({
                        issuer,
                        sector,
                        isin,
                        opportunityId,
                        bondCategory,
                        faceValue,
                        couponRate,
                        rating,
                    }) => ({
                        title: issuer,
                        isin,
                        tags: [sector, bondCategory].filter(Boolean),
                        dealDetails: [
                            {
                                label: 'Coupon Rate',
                                value: couponRate
                                    ? `${getNumberWithTwoDecimal(couponRate)} %`
                                    : '-',
                            },
                            {
                                label: 'Face Value',
                                value: faceValue
                                    ? getNumberAsCurrency(faceValue)
                                    : '-',
                            },
                        ],
                        footerDetails: [
                            {
                                label: 'Rating Category',
                                value: rating ? rating : '-',
                            },
                            {
                                label: 'ISIN',
                                value: isin ? isin : '-',
                            },
                        ],
                        isDealLive: opportunityId,
                    })
                );

                setSearchResults({
                    search: input,
                    results: { count: data.entity.count, rows: formattedData },
                    page,
                });
                setSearchQuery(input);
                router.push(
                    {
                        pathname: router.pathname,
                        query: { search: value, page },
                    },
                    undefined,
                    { shallow: true }
                );
            } else {
                setSearchResults({
                    search: input,
                    results: null,
                    page: 1,
                });
                setShowResultsPage(false);
            }
            setLoading({
                search: false,
                results: false,
                searchButton: false,
            });
        } catch (error) {
            setSearchResults({
                search: input,
                results: null,
                page: 1,
            });
            setShowResultsPage(false);
            setLoading({
                search: false,
                results: false,
                searchButton: false,
            });
        } finally {
            setLoading({
                search: false,
                results: false,
                searchButton: false,
            });
        }
    };

    const debouncedFetchBondDetails = useMemo(
        () =>
            debounce(
                (input, showSuggestions) =>
                    fetchSuggestions(input, 1, showSuggestions),
                300
            ),
        []
    );

    const handleShowSuggestions = (value, showSuggestions = true) => {
        setSearchQuery(value);

        if (value?.length >= 3) {
            setSearchError(null);
            setLoading((prev) => ({
                ...prev,
                search: showSuggestions,
            }));
            setSuggestions({
                search: value,
                showSuggestions: true,
                suggestions: [],
            });
            debouncedFetchBondDetails(value, showSuggestions);
        } else {
            setSuggestions({
                search: value,
                suggestions: null,
                showSuggestions: false,
            });
        }
    };

    const onSearchFocus = (value) => {
        handleShowSuggestions(value, value.length >= 3, true);
        if (width < 768) {
            setShowSearchModal(true);
        }
    };

    const handleOnSearch = (e) => {
        e.preventDefault();
        setSearchError(null);

        const value =
            width >= 768
                ? e.target['bondSearch'].value
                : e.target['bondSearchMobile'].value;

        setSearchQuery(value);
        const isISIN = isinRegexPattern.test(value);

        if (isISIN && suggestions?.suggestions?.length) {
            return router.push(routes.bonds.byIsin(value));
        }

        if (value.length >= 3) {
            fetchSearchResults(
                {
                    value,
                    pageNumber: 1,
                },
                true
            );
        } else {
            setSuggestions({
                search: value,
                suggestions: null,
                showSuggestions: true,
            });
            setSearchError(
                'Please enter at least 3 characters to start your search'
            );
        }
    };

    const fetchNextSuggestions = async () => {
        const isISIN = isinRegexPattern.test(searchQuery);
        const payload = {
            isin: isISIN ? searchQuery.toUpperCase() : null,
            issuer: !isISIN ? searchQuery : null,
            isIsin: isISIN,
            page: currentPage + 1,
            limit: 12,
        };

        const nextSuggestions = await getBondDetailsByIsin(payload);

        if (isValidResponseEntity(nextSuggestions)) {
            setCurrentPage((prevPage) => prevPage + 1);
            setSuggestions((prevSuggestions) => ({
                ...prevSuggestions,
                search: searchQuery,
                suggestions: prevSuggestions?.suggestions?.concat(
                    nextSuggestions.entity.rows
                ),
            }));
            if (
                suggestions.suggestions.length +
                    nextSuggestions.entity?.rows?.length >=
                nextSuggestions?.entity?.count
            ) {
                setHasMore(false);
            }
        }
    };

    const handleGoBack = () => {
        router.replace(
            {
                pathname: router.pathname,
            },
            undefined,
            { shallow: false }
        );
        setSearchQuery('');
        setShowResultsPage(false);
        setSearchResults({
            search: null,
            results: null,
            page: 1,
        });
        setSuggestions({
            search: null,
            suggestions: null,
            showSuggestions: false,
        });
    };

    useEffect(() => {
        const currentSearch = router.query.search;
        const previousSearch = searchResults?.search;
        const currentPage = Number(router.query.page);

        if (currentSearch && currentSearch !== previousSearch) {
            fetchSearchResults(
                {
                    value: currentSearch,
                    pageNumber: currentPage,
                },
                false
            );
        } else if (!currentSearch && previousSearch) {
            setShowResultsPage(false);
            setSuggestions({
                search: null,
                suggestions: null,
            });
            setSearchResults({
                search: null,
                results: null,
                page: 1,
            });
        }
    }, [router.query.search]);

    useEffect(() => {
        if (width > 768) {
            setShowSearchModal(false);
        }
    }, [width]);

    const states = {
        loading,
        errors,
        searchQuery,
        isIsin,
        showSearchModal,
        suggestions,
        showResultsPage,
        searchResults,
        currentPage,
        hasMore,
        searchError,
    };

    const callbacks = {
        handleOnChange: handleShowSuggestions,
        handleOnSubmit: handleOnSearch,
        onCloseSearchModal: () => setShowSearchModal(false),
        onOpenSearchModal: () => setShowSearchModal(true),
        handleOnFocus: onSearchFocus,
        handlePageChange,
        handleGoBack,
    };

    const services = {
        fetchSearchResults,
        fetchNextSuggestions,
    };

    return {
        states,
        callbacks,
        services,
    };
};

export default useBondDirectorySearch;
